/**
 * BuilderTooltip — lightweight hover tooltip for dark builder panels.
 * Renders above the trigger by default. Supports optional action link.
 */
import { useState, useRef } from "react"

export interface BuilderTooltipProps {
  /** The element that triggers the tooltip */
  children: React.ReactNode
  /** Tooltip text */
  text: string
  /** Optional clickable action label */
  actionLabel?: string
  onAction?: () => void
}

export function BuilderTooltip({
  children,
  text,
  actionLabel,
  onAction,
}: BuilderTooltipProps) {
  const [visible, setVisible] = useState(false)
  const ref = useRef<HTMLSpanElement>(null)

  return (
    <span
      ref={ref}
      style={{ position: "relative", display: "inline-flex" }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      {children}
      {visible && (
        <span
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(30,30,40,0.98)",
            border: "1px solid rgba(255,255,255,0.12)",
            borderRadius: 7,
            padding: "6px 10px",
            whiteSpace: "nowrap",
            zIndex: 2000,
            pointerEvents: actionLabel ? "auto" : "none",
            display: "flex",
            flexDirection: "column",
            gap: 4,
            boxShadow: "0 4px 16px rgba(0,0,0,0.5)",
          }}
        >
          {/* Arrow */}
          <span
            style={{
              position: "absolute",
              top: "100%",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "5px solid transparent",
              borderRight: "5px solid transparent",
              borderTop: "5px solid rgba(255,255,255,0.12)",
            }}
          />
          <span
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,0.75)",
              fontFamily: "'DM Sans', sans-serif",
              lineHeight: 1.4,
            }}
          >
            {text}
          </span>
          {actionLabel && onAction && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                onAction()
              }}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                fontSize: 11,
                color: "#60a5fa",
                fontFamily: "'DM Sans', sans-serif",
                textAlign: "left",
                textDecoration: "underline",
                textUnderlineOffset: 2,
              }}
            >
              {actionLabel}
            </button>
          )}
        </span>
      )}
    </span>
  )
}
